import { Icon, StatCard, cn, type IconName } from '@evogard/ui';
import type { ChartColor } from '@evogard/ui/chart';
import { financeDashboardChange, type Money } from '@evogard/domain';

/**
 * O cartão de um total do painel, com os doze meses atrás dele e a variação
 * contra o período anterior — o número, a forma da série e o "melhor ou pior
 * que antes" na mesma olhada.
 *
 * A variação diz a direção por seta E por palavra, e só depois por cor: na
 * despesa, subir é o lado ruim (`higherIsBetter = false`), e a cor inverte
 * junto, nunca sozinha.
 */
export function TrendTile({
  icon,
  label,
  legend,
  value,
  support,
  series,
  now,
  before,
  beforeLabel,
  higherIsBetter,
}: {
  icon: IconName;
  label: string;
  legend?: ChartColor;
  value: string | undefined;
  support?: string;
  /** Os doze meses, do mais velho para o mais novo, em centavos. */
  series: readonly number[] | undefined;
  now: Money | undefined;
  before: Money | undefined;
  /** "setembro de 2026", "01/10 a 10/10" — com o que a variação compara. */
  beforeLabel: string;
  higherIsBetter: boolean;
}) {
  const change =
    now === undefined || before === undefined
      ? undefined
      : financeDashboardChange(now, before);
  return (
    <StatCard
      icon={icon}
      label={label}
      legend={legend}
      value={value}
      support={support}
      footer={
        series === undefined ? undefined : (
          <div className="flex items-end justify-between gap-3">
            <Change
              change={change}
              beforeLabel={beforeLabel}
              higherIsBetter={higherIsBetter}
            />
            <Sparkline
              values={series}
              color={legend}
              label={`${label} nos últimos 12 meses`}
            />
          </div>
        )
      }
    />
  );
}

function Change({
  change,
  beforeLabel,
  higherIsBetter,
}: {
  change: number | null | undefined;
  beforeLabel: string;
  higherIsBetter: boolean;
}) {
  if (change === undefined) return null;
  if (change === null) return <span>nada em {beforeLabel}</span>;
  if (change === 0) return <span>igual a {beforeLabel}</span>;
  const up = change > 0;
  const good = up === higherIsBetter;
  return (
    <span className="flex min-w-0 items-center gap-1">
      <span
        className={cn(
          'flex items-center gap-0.5 font-semibold',
          good ? 'text-sucesso' : 'text-erro',
        )}
      >
        <Icon nome={up ? 'seta-cima' : 'seta-baixo'} size={16} />
        {Math.abs(change)}%
      </span>
      <span className="truncate">sobre {beforeLabel}</span>
    </span>
  );
}

/* As classes escritas por inteiro: o Tailwind só gera a classe que lê no código. */
const INK: Partial<Record<ChartColor, string>> = {
  'fase-azul': 'text-fase-azul',
  'fase-laranja': 'text-fase-laranja',
};

/**
 * A série em miniatura: uma linha de 2px com a área clara embaixo, sem eixo —
 * o que ela mostra é a FORMA dos doze meses, e o número exato está no
 * gráfico da tendência logo abaixo. O último mês ganha um ponto, que é onde
 * o número do cartão está.
 */
function Sparkline({
  values,
  color,
  label,
}: {
  values: readonly number[];
  color?: ChartColor;
  label: string;
}) {
  const W = 96;
  const H = 28;
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const x = (i: number) => (values.length === 1 ? W : (i / (values.length - 1)) * W);
  const y = (v: number) => H - 2 - ((v - min) / span) * (H - 4);
  const line = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const last = values.at(-1) ?? 0;
  return (
    <svg
      viewBox={`-3 -3 ${W + 6} ${H + 6}`}
      className={cn(
        'h-8 w-28 shrink-0 overflow-visible',
        (color && INK[color]) ?? 'text-texto-suave',
      )}
      role="img"
      aria-label={label}
    >
      <polygon
        points={`0,${H} ${line} ${W},${H}`}
        fill="currentColor"
        fillOpacity={0.12}
      />
      <polyline
        points={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle
        cx={x(values.length - 1)}
        cy={y(last)}
        r={3}
        fill="currentColor"
        className="stroke-superficie"
        strokeWidth={2}
      />
    </svg>
  );
}
